import React, { useRef, useState } from 'react';
import { motion, useInView } from 'motion/react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Sofa, Building2, Layers, Store, Hotel, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEOHead from '../components/SEOHead';

const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <div ref={ref} className="overflow-hidden">
      <motion.div initial={{ y: "100%", opacity: 0 }} animate={isInView ? { y: 0, opacity: 1 } : {}} transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
        {children}
      </motion.div>
    </div>
  );
};

const FAQItem = ({ q, a, open, onClick }) => (
  <div className="border-b border-[#FF6B35]/10 last:border-0">
    <button onClick={onClick} className="w-full py-5 flex items-center justify-between text-left group" aria-expanded={open}>
      <span className="text-[#2D2D2D] font-medium pr-4 group-hover:text-[#FF6B35] transition-colors">{q}</span>
      {open ? <ChevronUp size={18} className="text-[#FF6B35] flex-shrink-0" /> : <ChevronDown size={18} className="text-[#8A8A8A] flex-shrink-0" />}
    </button>
    <motion.div initial={false} animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }} className="overflow-hidden">
      <p className="pb-5 text-[#5A5A5A] text-sm leading-relaxed">{a}</p>
    </motion.div>
  </div>
);

const Interiors = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const services = [
    { icon: Sofa, title: 'Residential Interiors', desc: 'Thoughtfully designed living spaces that reflect your personality and lifestyle.', link: '/services/residential-interiors', points: ['Space planning', 'Material selection', 'Furniture curation', 'Lighting design'] },
    { icon: Building2, title: 'Commercial Interiors', desc: 'Corporate offices and headquarters that boost productivity and reflect brand culture.', link: '/services/commercial-interiors', points: ['Brand-aligned design', 'Ergonomic planning', 'Acoustic solutions', 'Biophilic elements'] },
    { icon: Layers, title: 'Workspace Design', desc: 'Modern workspaces optimized for collaboration, focus, and wellbeing.', link: '/services/workspace-design', points: ['Activity-based design', 'Collaboration zones', 'Focus areas', 'Wellness integration'] },
    { icon: Store, title: 'Retail Spaces', desc: 'Showrooms and stores that create immersive shopping experiences.', link: '/services/retail-spaces', points: ['Customer flow design', 'Visual merchandising', 'Brand experience', 'Fixture design'] },
    { icon: Hotel, title: 'Hospitality Design', desc: 'Hotels, restaurants, and cafes that tell a story through space.', link: '/services/hospitality-design', points: ['Atmosphere design', 'Guest experience', 'Kitchen planning', 'Outdoor spaces'] },
  ];

  const process = [
    { step: '01', title: 'Consultation', desc: 'Understanding your vision, functional needs, brand identity, and budget before anything gets designed.' },
    { step: '02', title: 'Concept Design', desc: 'Mood boards, spatial layouts, material palettes, and 3D visualizations to bring the concept to life.' },
    { step: '03', title: 'Detailed Design', desc: 'Technical drawings, furniture specifications, lighting plans, and material schedules for precise execution.' },
    { step: '04', title: 'Execution & Handover', desc: 'Vendor coordination, site supervision, quality checks, and final styling. We stay until it is perfect.' },
  ];

  const faqs = [
    { q: 'What types of commercial projects do you handle?', a: 'We design corporate offices, co-working spaces, retail stores, showrooms, restaurants, cafes, hotels, and mixed-use commercial spaces. Our team manages projects from 500 sq ft to 50,000 sq ft.' },
    { q: 'Can you design spaces that align with our brand identity?', a: 'This is our specialty. We start every project by understanding your brand — values, colors, tone, personality — and translate that into spatial design. Your office or store becomes a physical extension of your brand.' },
    { q: 'Do you provide end-to-end execution?', a: 'Yes. Our service covers concept design, 3D visualization, technical drawings, material sourcing, vendor management, site supervision, and final styling. One team from start to finish.' },
    { q: 'What is your typical project timeline?', a: 'Residential projects take 8-14 weeks from concept to handover. Commercial projects range from 12-20 weeks depending on scale. We provide detailed timelines during the consultation phase.' },
    { q: 'Do you work on projects in Dubai and India?', a: 'Yes. We have active projects in Chennai, Hyderabad, and Dubai. Our team travels for site visits and we maintain relationships with local vendors in each market.' },
  ];

  return (
    <Layout>
      <SEOHead
        title="Interior Design Solutions | Buzz Craft Maven"
        description="Residential, commercial, workspace, retail, and hospitality interior design. BCM creates spaces that reflect brand identity and enhance human experience."
        canonical="https://buzzcraftmaven.com/interiors"
        schemas={[
          { "@context": "https://schema.org", "@type": "Service", "name": "Interior Design Solutions", "provider": { "@type": "Organization", "name": "Buzz Craft Maven" }, "description": "Residential, commercial, workspace, retail, and hospitality interior design.", "areaServed": ["India", "Dubai", "UAE"] },
          { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://buzzcraftmaven.com" }, { "@type": "ListItem", "position": 2, "name": "Interior Solutions", "item": "https://buzzcraftmaven.com/interiors" }] },
          { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqs.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) }
        ]}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-sunrise-hero relative overflow-hidden">
        <div className="absolute top-10 right-[10%] w-[350px] h-[350px] sun-orb sun-orb-primary opacity-30" />
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <nav className="text-sm text-[#8A8A8A] mb-8" aria-label="Breadcrumb"><Link to="/" className="hover:text-[#FF6B35]">Home</Link> <span className="mx-2">/</span> <span className="text-[#2D2D2D]">Interior Solutions</span></nav>
          <div className="max-w-3xl">
            <TextReveal><h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D2D2D] tracking-tight leading-[1.05] mb-6">Spaces That Tell <span className="text-gradient-sunrise">Your Story.</span></h1></TextReveal>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-base md:text-lg text-[#5A5A5A] max-w-xl leading-relaxed mb-8">
              From residential havens to commercial landmarks — we design interiors that align with your brand identity and enhance how people experience your space.
            </motion.p>
            <Link to="/contact"><motion.button className="btn-sunrise px-8 py-3.5" whileHover={{ scale: 1.02 }}><span className="flex items-center gap-2">Book a Consultation <ArrowRight size={18} /></span></motion.button></Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-28 bg-[#FFFBF5]" aria-label="Interior services">
        <div className="container mx-auto px-6 md:px-12">
          <TextReveal><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D] mb-12">What We <span className="text-gradient-sunrise">Design.</span></h2></TextReveal>
          <div className="space-y-6">
            {services.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="p-6 md:p-8 bg-white rounded-2xl border border-[#FF6B35]/8 hover:border-[#FF6B35]/20 transition-all" data-testid={`interior-service-detail-${i}`}>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-12 h-12 rounded-xl bg-[#FF6B35]/8 flex items-center justify-center flex-shrink-0">
                    <s.icon size={22} className="text-[#FF6B35]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-xl font-semibold text-[#2D2D2D] mb-2">{s.title}</h3>
                    <p className="text-[#5A5A5A] text-sm leading-relaxed mb-4">{s.desc}</p>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {s.points.map(p => (
                        <div key={p} className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                          <CheckCircle size={14} className="text-[#FF6B35] flex-shrink-0" /> {p}
                        </div>
                      ))}
                    </div>
                    {s.link && <Link to={s.link} className="text-[#FF6B35] text-sm font-medium hover:underline inline-flex items-center gap-1">Learn more <ArrowRight size={14} /></Link>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 bg-white" aria-label="Our process">
        <div className="container mx-auto px-6 md:px-12">
          <TextReveal><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D] mb-12">Our Design <span className="text-gradient-sunrise">Process.</span></h2></TextReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p, i) => (
              <motion.div key={p.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="relative p-6 bg-[#FFFBF5] rounded-2xl border border-[#FF6B35]/8">
                <span className="font-display text-4xl font-bold text-[#FF6B35]/10 absolute top-3 right-4">{p.step}</span>
                <h3 className="font-semibold text-[#2D2D2D] mb-2 mt-2">{p.title}</h3>
                <p className="text-[#5A5A5A] text-sm">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#FFFBF5]" aria-label="FAQ">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl">
          <TextReveal><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D] mb-10 text-center">Questions About <span className="text-gradient-sunrise">Interior Design</span></h2></TextReveal>
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#FF6B35]/8">
            {faqs.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} open={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? null : i)} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#FF6B35] to-[#FFB627] text-center">
        <div className="container mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">Let's design your space.</h2>
          <p className="text-white/80 max-w-md mx-auto mb-8">Book a consultation and tell us about the space you envision.</p>
          <Link to="/contact"><motion.button className="px-10 py-4 bg-white text-[#FF6B35] rounded-full font-semibold shadow-xl hover:shadow-2xl transition-shadow inline-flex items-center gap-2" whileHover={{ scale: 1.02 }}>Book a Consultation <ArrowRight size={18} /></motion.button></Link>
        </div>
      </section>
    </Layout>
  );
};

export default Interiors;
